import type { EnnismoreTheme } from '@/ui/theme';

const fonts = {
  headline: 'Trade Gothic, serif',
  intro: 'Trade Gothic, sans-serif',
  subheader: 'Trade Gothic, sans-serif',
  body: 'Trade Gothic, sans-serif',
};

export const twentyFiveHoursTheme: EnnismoreTheme = {
  themeKey: '25hours',
  logoUrl: '/brands/25hours/images/logo.svg',
  faviconUrl: '/brands/25hours/icons/favicon.ico',
  coBrandedDisloyaltyLogoUrl: '/disloyalty/brands/25hours.svg',
  space: ['0px', '8px', '16px', '20px', '24px', '32px', '40px', '56px'],
  fonts,
  text: {
    heading1: {
      fontFamily: fonts.headline,
      fontWeight: 700,
      fontSize: '54px',
      lineHeight: '32px',
      textTransform: 'lowercase',
    },
    heading2: {
      fontFamily: fonts.subheader,
      fontSize: '56px',
      lineHeight: '64px',
    },
    heading3: {
      fontFamily: fonts.subheader,
      fontSize: '40px',
      lineHeight: '48px',
    },
    heading4: {
      fontFamily: fonts.subheader,
      fontSize: '32px',
      lineHeight: '40px',
    },
    heading5: {
      fontFamily: fonts.subheader,
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: 700,
    },
    heading6: {
      fontFamily: fonts.subheader,
      fontSize: '18px',
      lineHeight: '24px',
    },
    intro1: {
      fontFamily: fonts.intro,
      fontSize: '40px',
      lineHeight: '56px',
    },
    intro2: {
      fontFamily: fonts.intro,
      fontSize: '24px',
      lineHeight: '40px',
    },
    subheader1: {
      fontFamily: fonts.subheader,
      fontSize: '24px',
      lineHeight: '32px',
    },
    subheader2: {
      fontFamily: fonts.subheader,
      fontSize: '18px',
      lineHeight: '32px',
    },
    subheader3: {
      fontFamily: fonts.subheader,
      fontSize: '14px',
      lineHeight: '24px',
    },
    bodyLarge: {
      fontFamily: fonts.body,
      fontSize: '20px',
      lineHeight: '32px',
    },
    bodyCopy: {
      fontFamily: fonts.body,
      fontSize: '18px',
      lineHeight: '28px',
    },
    bodySmall: {
      fontFamily: fonts.body,
      fontSize: '16px',
      lineHeight: '24px',
    },
    bodyExtraSmall: {
      fontFamily: fonts.body,
      fontSize: '11px',
      lineHeight: '22px',
    },
    captions: {
      fontFamily: fonts.body,
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
  colors: {
    white: '#fff',
    textPrimary: '#000',
    textSecondary: '#757575',
    background: '#FBFBFB',
    primary100: '#f7f7e1', // glen only - ignore
    primary200: '#f7f7e1',
    primary300: '#2d2926',
    primary400: '#FFE7D9', // hox only
    primary500: '#2d2926',
    secondary100: '#f6f6f6',
    secondary200: '#d9d9d9',
    secondary300: '#2d2926',
    secondary400: '#2d2926',
    error: '#B00020',
    sale: '#B00020',
  },
  buttons: {
    primary: {
      backgroundColor: '#BCCF00',
      color: 'black',
      fontFamily: fonts.subheader,
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '24px',
      border: '2px solid black',
      borderRadius: 60,
      transition: 'background-color .1s ease-out,box-shadow .1s ease-out',
      textTransform: 'capitalize',
      ':hover': {
        backgroundColor: '#c9da31',
        boxShadow: '2px 4px 6px rgba(0, 0, 0, 0.4)',
      },
    },
    secondary: {
      backgroundColor: 'white',
      color: 'black',
      fontFamily: fonts.subheader,
      textTransform: 'capitalize',
      fontSize: '20px',
      borderRadius: 60,
      border: '2px solid black',
      transition: 'box-shadow .1s ease-out',
      ':hover': {
        boxShadow: '2px 4px 6px rgba(0, 0, 0, 0.4)',
      },
    },
  },
  keylines: {
    border: '2px solid black',
  },
  forms: {
    heading: {
      fontSize: '26px',
      fontWeight: 700,
      lineHeight: '32px',
      paddingBottom: '16px',
    },
    input: {
      borderRadius: '60px',
      fontSize: '16px',
      fontWeight: 700,
      position: 'relative',
      padding: '24px 22px 8px',
      lineHeight: '22px',
      placeholder: {
        color: '#8D8C8C',
        fontWeight: 700,
      },
      backgroundColor: 'white',
    },
    textarea: {
      borderRadius: '20px',
    },
    label: {
      fontSize: '16px',
      position: 'absolute',
      paddingBottom: '0px',
      paddingLeft: '24px',
      top: '8px',
      lineHeight: '19.2px',
    },
    radioGroup: {
      divider: { border: '0.5px solid #8D8C8C' },
    },
    radioButton: {
      selected: {
        backgroundColor: 'transparent',
        padding: '24px 0px 32px',
      },
      borderBottom: '0.5px solid #8D8C8C',
      padding: '24px 0px 32px',
      collapsibleContent: {
        padding: '8px 0 0 0',
      },
      description: {
        padding: '8px 0 0 0',
      },
    },
    searchForm: {
      padding: '60px 40px',
      mobile: {
        padding: '40px 16px',
        margin: '0 16px',
      },
    },
  },
  fontFaceDefinitions: [
    {
      fontFamily: 'Trade Gothic',
      fontWeight: 400,
      sources: {
        woff: '/brands/25hours/fonts/tradegothicltstd-cn18-webfont.woff2',
      },
    },
    {
      fontFamily: 'Trade Gothic',
      fontWeight: 700,
      sources: {
        woff: '/brands/25hours/fonts/tradegothicltstd-bdcn20-webfont.woff2',
      },
    },
  ],
  componentProperties: {
    page: {
      background: 'url(/brands/25hours/images/dots-background.webp) repeat',
      title: {
        mobile: {
          fontSize: '34px',
          textTransform: 'lowercase',
        },
      },
      stepLabel: {
        fontSize: '14px',
      },
    },
    confirmation: {
      mobile: {
        rowGap: '24px',
      },
      title: {
        fontSize: '54px',
        fontWeight: 700,
        mobile: {
          fontSize: '34px',
        },
      },
      bookingConfirmationAdditionalDetailsContainer: {
        margin: '0 16px',
        mobile: {
          padding: '32px 16px',
          margin: '0 16px',
        },
        padding: '36px 24px 42px',
        border: '1px solid #8D8C8C',
        divider: {
          borderColor: '#8D8C8C',
          borderWidth: '1px',
        },
      },
      bookingConfirmationDetailsCard: {
        bookingNumberHeading: {
          fontSize: '26px',
        },
        addressHeading: {
          fontSize: '16px',
        },
        totalDataRow: {
          label: {
            fontWeight: 400,
            fontSize: '14px',
          },
          value: {
            fontSize: '14px',
          },
        },
        manageMyBooking: {
          title: {
            fontWeight: 700,
            paddingBottom: '8px',
          },
          description: {
            fontSize: '14px',
            paddingBottom: '8px',
          },
          link: {
            fontSize: '14px',
            fontWeight: 700,
            fontFamily: fonts.subheader,
          },
        },
        paymentCancellationPolicy: {
          title: {
            paddingBottom: '4px',
          },
          description: {
            fontSize: '14px',
            color: 'black',
            paddingBottom: '8px',
          },
        },
        termsAndConditions: {
          fontSize: '14px',
        },
      },
      anyQuestionsCard: {
        margin: '0 16px',
        padding: '36px 24px 42px',
        title: {
          fontSize: '26px',
        },
        description: {
          fontSize: '16px',
        },
        border: '1px solid #8D8C8C',
        mobile: {
          margin: '0 16px',
          padding: '32px 16px',
        },
      },
      restaurantsCard: {
        margin: '0 16px',
        title: {
          fontSize: '26px',
        },
        restaurantTitle: {
          fontSize: '20px',
          fontWeight: 700,
        },
        restaurantDescription: {
          fontSize: '16px',
        },
        padding: '36px 24px 42px',
        border: '1px solid #8D8C8C',
        mobile: {
          margin: '0 16px',
          padding: '32px 16px',
        },
      },
      whereToFindUsCard: {
        margin: '0 16px',
        padding: '36px 24px 42px',
        border: '1px solid #8D8C8C',
        mobile: {
          margin: '0 16px',
          padding: '32px 16px',
        },
      },
      header: {
        background:
          'url(/brands/25hours/images/texture-tiles.png) lightgray 0% 0% / 100px 100px repeat',
        borderBottom: '1px solid #8D8C8C',
        paddingBottom: '52px',
        mobile: {
          paddingBottom: '36px',
        },
      },
      headerImage: {
        container: {
          width: '558px',
          margin: '100px auto 72px',
          mobile: {
            width: '100%',
            margin: '48px auto 48px',
            padding: '0 48px',
          },
        },
      },
      confirmationMessage: {
        fontSize: '16px',
        subtitle: {
          paddingBottom: '0',
        },
        mobile: {
          padding: '16px 16px 0',
        },
      },
      bookingConfirmationContainer: {
        margin: '60px auto auto auto',
        mobile: {
          margin: '40px auto auto auto',
        },
      },
      page: {
        background: 'url(/brands/25hours/images/dots-background.webp) repeat',
      },
    },
    cardFlushDivider: {
      border: '1px solid #8D8C8C',
      marginInlineStart: '0px',
      marginInlineEnd: '0px',
    },
    bedroomCard: {
      border: '2px solid black',
      roomTitle: {
        fontSize: '26px',
      },
      ctaButton: {
        marginTop: '16px',
      },
      roomSpecItem: {
        lineHeight: '19.6px',
        color: '#8D8C8C',
        value: {
          color: 'black',
        },
      },
      roomSpecItemsList: {
        padding: '24px 0px',
      },
      description: {
        padding: '16px 0px',
      },
      roomRateName: {
        fontWeight: 700,
        lineHeight: '26px',
        paddingBottom: '8px',
      },
      totalPrice: {
        fontSize: '24px',
        lineHeight: '32px',
        fontWeight: 700,
      },
      totalLabel: {
        fontSize: '14px',
        color: '#8D8C8C',
      },
      perNightPriceLabel: {
        fontSize: '14px',
        color: '#8D8C8C',
      },
      mobile: {
        padding: '0px 16px 40px',
        border: 'none',
      },
    },
    bookingManagement: {
      title: {
        textTransform: 'lowercase',
        fontWeight: 700,
        fontSize: '54px',
        lineHeight: '54px',
        paddingBottom: '32px',
        mobile: {
          fontSize: '34px',
          lineHeight: '32px',
        },
      },
      bookingCard: {
        border: 'none',
        header: {
          backgroundColor: 'white',
          cancelled: {
            backgroundColor: 'white',
            pillowBorder: 'none',
          },
          fontSize: '14px',
          fontWeight: 700,
          borderBottom: '1px solid #8D8C8C',
        },
        divider: {
          borderColor: '#8D8C8C',
          borderWidth: '1px',
        },
        button: {
          variant: 'secondary',
        },
        mobile: {
          border: '1px solid #8D8C8C',
        },
        cancellationModal: {
          borderRadius: '0',
          border: 'none',
          textAlign: 'center',
          padding: '80px 124px',
          maxWidth: '772px',
          title: {
            fontSize: '54px',
            fontWeight: 700,
            lineHeight: '54px',
            textTransform: 'lowercase',
          },
          mobile: {
            padding: '40px 16px 60px',
          },
        },
      },
    },
    staySummary: {
      border: '1px solid #8D8C8C',
      title: {
        fontSize: '26px',
        fontWeight: 700,
        lineHeight: '32px',
      },
      dataRow: {
        label: { opacity: 1, fontSize: '14px', lineHeight: '32px' },
        value: {
          fontSize: '14px',
          fontWeight: 700,
          lineHeight: '32px',
        },
      },
      dropdown: {
        fontSize: '16px',
        opacity: 1,
        fontWeight: 700,
      },
      divider: {
        borderColor: '#8D8C8C',
        borderWidth: '1px',
        marginInlineEnd: '24px',
        marginInlineStart: '24px',
        mobile: {
          marginInlineEnd: '16px',
          marginInlineStart: '16px',
        },
      },
      summarySection: {
        padding: '32px 24px',
        mobile: {
          padding: '32px 16px',
        },
      },
    },
    imageSlider: {
      paginator: {
        color: 'black',
      },
      mobile: {
        width: 'calc(100% + 32px)',
        marginInlineStart: '-16px',
      },
    },
    alertCard: {
      icon: {
        backgroundColor: '#BCCF00',
      },
    },
    paymentMethods: {
      googlePay: {
        style: 'light',
        borderRadius: '60px',
        border: '2px solid black',
      },
      applePay: {
        style: 'light',
        borderRadius: '60px',
        border: '2px solid black',
      },
    },
    addOnList: {
      topDivider: {
        borderTop: '1px solid #8D8C8C',
        marginBottom: '40px',
      },
    },
    addOnCard: {
      backgroundColor: '#FFF',
      added: {
        border: '2px solid black',
      },
      divider: {
        borderColor: '#8D8C8C',
        borderWidth: '1px',
      },
      disclaimer: {
        borderRadius: '0',
        backgroundColor: 'white',
        border: '1px solid #8D8C8C',
        padding: '22px 22px 36px',
        marginBottom: '16px',
        warning: {
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: '19.2px',
          letterSpacing: '0.32px',
          paddingBottom: '8px',
        },
      },
      title: {
        fontSize: '18px',
        lineHeight: '26px',
        fontWeight: 700,
      },
      subTitle: {
        fontSize: '18px',
        lineHeight: '26px',
        fontWeight: 700,
      },
      ctaLabel: {
        fontSize: '16px',
      },
    },
    halfScreenLayout: {
      borderRight: 'none',
    },
    checkoutSidebar: {
      cancellationPolicy: {
        borderRadius: '6px',
        backgroundColor: 'transparent',
        border: '1px solid #8D8C8C',
        title: {
          fontSize: '18px',
          fontWeight: 700,
          lineHeight: '26px',
        },
      },
    },
    search: {
      collapsibleForm: {
        searchForm: {
          border: '2px solid black',
          borderTop: 'none',
          padding: '60px 40px',
          mobile: {
            padding: '40px 16px',
          },
        },
        searchSummary: {
          border: '2px solid black',
          expanded: {
            borderBottom: 'none',
          },
          copy: {
            fontSize: '16px',
          },
          modifySearchButton: {
            fontSize: '16px',
            fontWeight: 700,
          },
          mobile: {
            title: {
              textTransform: 'lowercase',
              fontSize: '18px',
            },
            padding: '12px 16px',
          },
        },
      },
      results: {
        mobile: {
          paddingBottom: '0px',
        },
        bedroomAvailabilityList: {
          rowGap: '40px',
          mobile: {
            rowGap: '44px',
          },
        },
        sidebarContainer: {
          cancellationPolicy: {
            borderRadius: '6px',
            border: '1px solid #8D8C8C',
            backgroundColor: 'transparent',
            title: {
              fontWeight: 700,
              lineHeight: '26px',
            },
            color: 'black',
            mobile: {
              fontWeight: 700,
              color: 'black',
            },
          },
          hotelPerks: {
            backgroundColor: 'white',
            border: 'none',
            mobile: {
              backgroundColor: 'white',
              title: {
                fontSize: '20px',
                lineHeight: '38px',
              },
              button: {
                fontWeight: 700,
              },
              modal: {
                backgroundColor: 'white',
              },
            },
          },
        },
      },
    },
    modals: {
      title: {
        fontSize: '54px',
        textTransform: 'lowercase',
        fontWeight: 700,
        lineHeight: '54px',
      },
      fontSize: '16px',
    },
    hotelPerk: {
      gap: '8px',
      title: {
        fontWeight: 700,
        fontSize: '14px',
        letterSpacing: '0.28px',
        lineHeight: '20px',
        mobile: {
          fontSize: '14px',
        },
      },
      description: {
        fontSize: '14px',
        letterSpacing: '0.28px',
        lineHeight: '22px',
        mobile: {
          fontSize: '14px',
        },
      },
    },
    calendar: {
      border: '2px solid black',
      borderRadius: '30px',
      header: {
        nav: {
          hover: {
            backgroundColor: '#9d708a96',
          },
        },
        caption: {
          fontSize: '16px',
          lineHeight: '26px',
          letterSpacing: '0.32px',
          fontWeight: 700,
        },
        arrow: {
          stroke: 'transparent',
        },
      },
      body: {
        date: {
          hover: {
            backgroundColor: '#9d708a96',
          },
          selected: {
            backgroundColor: 'black',
          },
          selectedMiddle: {
            backgroundColor: '#9D708A',
            color: 'black',
          },
        },
      },
    },
    header: {
      height: 90,
      backgroundColor: 'black',
      wizard: {
        step: {
          opacity: 1,
          color: 'white',
          active: {
            fontWeight: 700,
          },
          mobile: {
            fontSize: '12px',
          },
        },
      },
      logo: {
        borderRight: 'none',
      },
      languageAndCurrencySelector: {
        color: 'white',
        fontFamily: fonts.subheader,
        dropdown: {
          display: 'none',
        },
        divider: {
          borderWidth: '1px',
        },
        mobile: {
          fontSize: '12px',
        },
      },
    },
    sidebarCards: {
      primary: {
        borderRadius: '0px',
        borderColor: 'white',
      },
      secondary: {
        borderRadius: '0px',
        borderColor: 'primary300',
      },
    },
    footer: {
      textDecoration: 'none',
      borderTop: 'none',
      backgroundImage: 'url(/brands/25hours/images/noise-pattern.webp)',
      backgroundBlendMode: 'multiply',
      letterSpacing: '0.32px',
      lineHeight: '26px',
      copyright: {
        fontSize: '14px',
        letterSpacing: '0.28px',
        lineHeight: '22px',
      },
      link: {
        mobile: {
          gap: '28px',
        },
      },
    },
    cards: {
      primary: {
        divider: {
          borderTop: '0.5px solid #8D8C8C',
        },
      },
    },
  },
  icons: {},
  __temporary: {
    displayCarTransportOnNewLine: true,
  },
};
